import { useState } from "react";
import { Medal, TrendingUp, ChevronDown, RefreshCw } from "lucide-react";
import { useRankings } from "@/hooks/useRankings";
import type { Surfer } from "@/data/rankings";

const placeStyle = (p: number | "—") => {
  if (p === 1) return "bg-gradient-sunset text-accent-foreground";
  if (p === "—") return "bg-muted/50 text-muted-foreground";
  if (typeof p === "number" && p <= 3) return "bg-primary/20 text-primary border border-primary/40";
  return "bg-background border border-border text-foreground";
};

const Row = ({ s }: { s: Surfer }) => {
  const [open, setOpen] = useState(false);
  const medal =
    s.rank === 1
      ? "bg-gradient-sunset text-accent-foreground shadow-glow"
      : s.rank <= 3
      ? "bg-primary/20 text-primary border border-primary/40"
      : "bg-muted text-muted-foreground";

  return (
    <article className="rounded-2xl bg-background/60 border border-border hover:border-primary/40 transition-colors overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start gap-3 p-3 text-left"
      >
        <div
          className={`h-10 w-10 shrink-0 rounded-xl flex items-center justify-center font-display text-lg ${medal}`}
        >
          {s.rank}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xl leading-none">{s.flag}</span>
                <h3 className="font-display text-base md:text-lg text-foreground break-words leading-tight">
                  {s.name}
                </h3>
              </div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">
                {s.events} events
              </div>
            </div>
            <div className="text-right shrink-0 flex flex-col items-end">
              <div className="font-display text-lg text-foreground leading-none">
                {s.points.toLocaleString()}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
                pts
              </div>
            </div>
          </div>
          <div className="text-xs text-muted-foreground mt-1.5 flex items-center justify-between gap-2">
            <span className="truncate">
              {s.country} · {s.bestResult}
            </span>
            <ChevronDown
              className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
            />
          </div>
        </div>
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-border/60">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
            Event results
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
            {s.results.map((r) => (
              <div
                key={r.event}
                className="flex items-center justify-between gap-2 rounded-lg bg-card/60 px-2 py-1.5 border border-border/60"
              >
                <span className="text-xs text-muted-foreground truncate">{r.event}</span>
                <span
                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md min-w-6 text-center ${placeStyle(r.place)}`}
                >
                  {r.place === "—" ? "—" : `${r.place}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </article>
  );
};

export const Rankings = () => {
  const [tour, setTour] = useState<"men" | "women">("men");
  const { men, women, meta, isLoading, isValidating, refresh } = useRankings();
  const list = tour === "men" ? men : women;

  return (
    <div className="relative rounded-3xl bg-card/80 backdrop-blur-md border border-border shadow-soft p-6">
      {/* Refresh button — top right */}
      <button
        onClick={() => refresh()}
        title="Refresh rankings"
        className="absolute top-4 right-4 h-8 w-8 rounded-full flex items-center justify-center hover:bg-primary/10 transition-colors"
      >
        <RefreshCw
          className={`h-4 w-4 text-muted-foreground ${isLoading || isValidating ? "animate-spin" : ""}`}
        />
      </button>

      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div className="h-10 w-10 rounded-2xl bg-gradient-sunset flex items-center justify-center shadow-glow">
          <Medal className="h-5 w-5 text-accent-foreground" />
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold flex items-center gap-1">
            <TrendingUp className="h-3 w-3" /> CT Rankings 2026
          </div>
          <h2 className="font-display text-xl sm:text-2xl md:text-3xl text-foreground">
            Top 10 on Tour
          </h2>
        </div>
      </div>

      {/* Live meta strip — auto-updates from rankingsMeta */}
      <div className="flex items-center gap-1.5 mb-4 px-1">
        <RefreshCw className="h-3 w-3 text-muted-foreground shrink-0" />
        <p className="text-[11px] text-muted-foreground">
          After Event {meta.afterEvent} · {meta.lastEventName} ·{" "}
          <span className="text-primary font-medium">
            Next: {meta.nextEventName} — {meta.nextEventWindow}
          </span>{" "}
          ·{" "}
          <a
            href="https://www.worldsurfleague.com/athletes/tour/mct"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-foreground transition-colors"
          >
            Live standings ↗
          </a>
        </p>
      </div>

      {/* Tour toggle */}
      <div className="inline-flex rounded-full bg-muted p-1 mb-4">
        {(["men", "women"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTour(t)}
            className={`px-4 py-1.5 text-sm font-semibold rounded-full capitalize transition-colors ${
              tour === t
                ? "bg-gradient-sunset text-accent-foreground shadow-glow"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Rankings list */}
      <div className="grid gap-2 md:grid-cols-2">
        {list.map((s) => (
          <Row key={s.rank} s={s} />
        ))}
      </div>
    </div>
  );
};
